/**
 * Regras PURAS das comissoes: reversao por politica de estorno, faixa de
 * valor e o filtro do extrato. Usadas pelo gerador simulado (que faz o papel
 * do servidor) e pelas simulacoes "e se" da tela -- uma regra so, para a
 * simulacao nunca discordar do extrato.
 *
 * Decisoes nao obvias:
 *  1. `reverteComissao` e a traducao literal do ADR-001 E2: reembolso NAO
 *     reverte, chargeback reverte, politica por empresa; reembolso PARCIAL
 *     (valor menor que o pago) nunca reverte, em nenhuma politica. Trocar a
 *     ordem dos `case` muda a politica de cobranca do cliente.
 *  2. O filtro recebe uma dimensao para IGNORAR (`exceto`): e o que permite
 *     calcular a contagem de cada opcao de um filtro aplicando todos os
 *     outros (faceta), sem uma segunda funcao que possa divergir desta.
 *  3. Faixas de valor em centavos inteiros com limite inferior inclusivo:
 *     R$ 100,00 exatos cai em "R$ 100 a 499", nunca em duas faixas.
 *  4. A busca ignora acento e caixa: "patricia" acha "Patrícia".
 */

import type { RefundPolicy } from '../../types';
import type { AmountBand, ClosedByFilter, ContactChannel, ConversionFilters, ConversionRow, ConversionStatus, RefundBucket, RefundFilter, RefundKind } from './types';

export const POLITICA_PADRAO: RefundPolicy = 'reverte_chargeback';

export const ORDEM_POLITICAS: readonly RefundPolicy[] = ['nao_reverte', 'reverte_chargeback', 'reverte_tudo'];

export const ORDEM_ESTORNOS: readonly RefundKind[] = ['reembolso', 'reembolso_parcial', 'chargeback'];

/** A politica desfaz a comissao deste tipo de estorno? (ADR-001 E2) */
export function reverteComissao(politica: RefundPolicy, tipo: RefundKind): boolean {
  if (tipo === 'reembolso_parcial') {
    return false;
  }
  switch (politica) {
    case 'nao_reverte':
      return false;
    case 'reverte_chargeback':
      return tipo === 'chargeback';
    case 'reverte_tudo':
      return true;
  }
}

/** Comissao que sairia da apuracao se TODOS os estornos do recorte seguissem `politica`. */
export function comissaoRevertidaSob(
  politica: RefundPolicy,
  estornos: Readonly<Record<RefundKind, RefundBucket>>,
): { readonly cents: number; readonly count: number } {
  return ORDEM_ESTORNOS.reduce(
    (acc, tipo) =>
      reverteComissao(politica, tipo)
        ? { cents: acc.cents + estornos[tipo].commissionAtStakeCents, count: acc.count + estornos[tipo].count }
        : acc,
    { cents: 0, count: 0 },
  );
}

export type FaixaDeValor = Exclude<AmountBand, 'todas'>;

export const ORDEM_FAIXAS: readonly FaixaDeValor[] = ['ate_100', 'de_100_a_500', 'de_500_a_1000', 'acima_1000'];

/** Limite inferior inclusivo de cada faixa, em centavos. */
const PISO_DA_FAIXA: Readonly<Record<FaixaDeValor, number>> = {
  ate_100: 0,
  de_100_a_500: 10_000,
  de_500_a_1000: 50_000,
  acima_1000: 100_000,
};

export function faixaDoValor(cents: number): FaixaDeValor {
  if (cents >= PISO_DA_FAIXA.acima_1000) {
    return 'acima_1000';
  }
  if (cents >= PISO_DA_FAIXA.de_500_a_1000) {
    return 'de_500_a_1000';
  }
  if (cents >= PISO_DA_FAIXA.de_100_a_500) {
    return 'de_100_a_500';
  }
  return 'ate_100';
}

export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .trim();
}

/** Dimensoes do extrato com contagem por opcao. */
export type DimensaoDoFiltro = 'channel' | 'status' | 'closedBy' | 'refund' | 'amount';

function passaFechadoPor(row: ConversionRow, filtro: ConversionFilters['closedBy']): boolean {
  switch (filtro) {
    case 'todos':
      return true;
    case 'motor':
      return row.closedBy === null;
    case 'comercial':
      return row.closedBy !== null;
    case 'comercial_webhook':
      return row.closedBy !== null && row.closedBy.via === 'webhook';
    case 'venda_assistida':
      return row.closedBy !== null && row.closedBy.via === 'venda_assistida';
  }
}

function passaEstorno(row: ConversionRow, filtro: ConversionFilters['refund']): boolean {
  switch (filtro) {
    case 'todos':
      return true;
    case 'sem_estorno':
      return row.refund === null;
    case 'com_estorno':
      return row.refund !== null;
    case 'mantida':
      return row.refund !== null && !row.refund.reversed;
    case 'revertida':
      return row.refund !== null && row.refund.reversed;
  }
}

function textoPesquisavel(row: ConversionRow): string {
  return normalizar(
    [row.leadName ?? '', row.offerName, row.companyName, row.closedBy?.sellerName ?? '', row.closedBy?.orderRef ?? '', row.id].join(' '),
  );
}

/**
 * A linha passa em todos os filtros, menos `exceto`? `termo` chega ja
 * normalizado (uma vez por consulta, nao por linha).
 */
export function passaNosFiltros(row: ConversionRow, filtros: ConversionFilters, termo: string, exceto: DimensaoDoFiltro | null = null): boolean {
  if (exceto !== 'channel' && filtros.channel !== 'todos' && row.creditedContact.channel !== filtros.channel) {
    return false;
  }
  if (exceto !== 'status' && filtros.status !== 'todos' && row.status !== filtros.status) {
    return false;
  }
  if (exceto !== 'closedBy' && !passaFechadoPor(row, filtros.closedBy)) {
    return false;
  }
  if (exceto !== 'refund' && !passaEstorno(row, filtros.refund)) {
    return false;
  }
  if (exceto !== 'amount' && filtros.amount !== 'todas' && faixaDoValor(row.paidAmountCents) !== filtros.amount) {
    return false;
  }
  return termo === '' || textoPesquisavel(row).includes(termo);
}

/** Opcoes em que a linha conta, por dimensao (uma linha comercial conta em "comercial" E na sua entrada; um estorno, em "com estorno" E na consequencia). */
export function opcoesDaLinha(row: ConversionRow): {
  readonly channel: ContactChannel;
  readonly status: ConversionStatus;
  readonly closedBy: readonly Exclude<ClosedByFilter, 'todos'>[];
  readonly refund: readonly Exclude<RefundFilter, 'todos'>[];
  readonly amount: FaixaDeValor;
} {
  const fechadoPor: readonly Exclude<ClosedByFilter, 'todos'>[] =
    row.closedBy === null ? ['motor'] : ['comercial', row.closedBy.via === 'webhook' ? 'comercial_webhook' : 'venda_assistida'];
  return {
    channel: row.creditedContact.channel,
    status: row.status,
    closedBy: fechadoPor,
    refund: row.refund === null ? ['sem_estorno'] : ['com_estorno', row.refund.reversed ? 'revertida' : 'mantida'],
    amount: faixaDoValor(row.paidAmountCents),
  };
}
